import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as roleService from '../services/roleService';
import { DataTable, Field, FormSection, Modal, Notice } from './patterns';
import type { DataTableColumn } from './patterns';
import { MODULE_REGISTRY } from '../modules/registry';
import './RoleManagement.css';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Role {
  _id: string;
  name: string;
  description?: string;
  menus: string[];
  isFarmAdministrator: boolean;
}

// ─── Role Editor sub-component ───────────────────────────────────────────────

function RoleEditor({ role, onChange }: { role: Role; onChange: (next: Role) => void }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.startsWith('af') ? 'af' : 'en') as 'af' | 'en';

  const menus = useMemo(() => role.menus || [], [role.menus]);

  // Configurable menus only — defaultGranted menus (e.g. settings:user, which
  // backs the language switcher in AppShell chrome) are always granted and
  // not exposed in the role editor. A module with zero configurable menus is
  // hidden from the accordion entirely.
  const tenantModules = useMemo(
    () =>
      Object.values(MODULE_REGISTRY)
        .filter(mod => !mod.superadminOnly)
        .map(mod => ({ ...mod, menus: mod.menus.filter(m => !m.defaultGranted) }))
        .filter(mod => mod.menus.length > 0),
    []
  );

  const moduleStats = useMemo(() => {
    const out: Record<string, { granted: number; total: number }> = {};
    for (const mod of tenantModules) {
      const granted = mod.menus.filter(m => menus.includes(`${mod.key}:${m.key}`)).length;
      out[mod.key] = { granted, total: mod.menus.length };
    }
    return out;
  }, [tenantModules, menus]);

  // Manual overrides take precedence over auto-expand-when-any-granted.
  const [manualExpanded, setManualExpanded] = useState<Record<string, boolean>>({});

  const isExpanded = (moduleKey: string) => {
    if (moduleKey in manualExpanded) return manualExpanded[moduleKey];
    return (moduleStats[moduleKey]?.granted ?? 0) > 0;
  };

  const toggleExpanded = (moduleKey: string) => {
    setManualExpanded(prev => ({ ...prev, [moduleKey]: !isExpanded(moduleKey) }));
  };

  if (role.isFarmAdministrator) {
    return (
      <Notice tone="info">
        Farm Administrator has full access to all modules. This role cannot be edited.
      </Notice>
    );
  }

  return (
    <div className="role-editor" data-testid="role-editor">
      {tenantModules.map(mod => {
        const expanded = isExpanded(mod.key);
        const { granted, total } = moduleStats[mod.key];
        return (
          <div key={mod.key} className="role-editor__module">
            <button
              type="button"
              className="role-editor__module-header"
              onClick={() => toggleExpanded(mod.key)}
              aria-expanded={expanded}
            >
              <span className="role-editor__caret" aria-hidden="true">
                {expanded ? '▼' : '▶'}
              </span>
              <span className="role-editor__module-label">{mod.label[lang]}</span>
              <span
                className={
                  granted > 0
                    ? 'role-editor__counter role-editor__counter--has-grants'
                    : 'role-editor__counter'
                }
              >
                {t('roleManagement.menuCount', { granted, total })}
              </span>
            </button>
            {expanded && (
              <div className="role-editor__menus">
                {mod.menus.map(menu => {
                  const key = `${mod.key}:${menu.key}`;
                  const checked = menus.includes(key);
                  return (
                    <label key={key} className="role-editor__menu">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => {
                          const next = e.target.checked
                            ? Array.from(new Set([...menus, key]))
                            : menus.filter(m => m !== key);
                          onChange({ ...role, menus: next });
                        }}
                      />
                      <span className="role-editor__menu-label">{menu.label[lang]}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const RoleManagement: React.FC = () => {
  const { t } = useTranslation();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<Role>({
    _id: '',
    name: '',
    description: '',
    menus: [],
    isFarmAdministrator: false,
  });

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await roleService.listRoles();
      setRoles(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleOpenModal = (role: Role | null = null) => {
    if (role) {
      setFormData({
        _id: role._id,
        name: role.name,
        description: role.description || '',
        menus: role.menus || [],
        isFarmAdministrator: Boolean(role.isFarmAdministrator),
      });
    } else {
      setFormData({
        _id: '',
        name: '',
        description: '',
        menus: [],
        isFarmAdministrator: false,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        menus: formData.menus,
      };

      if (formData._id) {
        await roleService.updateRole(formData._id, payload);
      } else {
        await roleService.createRole(payload);
      }
      handleCloseModal();
      fetchRoles();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save role');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('roleManagement.deleteConfirm'))) {
      try {
        await roleService.deleteRole(id);
        fetchRoles();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete role');
      }
    }
  };

  const columns: DataTableColumn<Role>[] = [
    {
      key: 'name',
      label: t('roleManagement.nameHeader'),
      render: (role) => role.name,
    },
    {
      key: 'description',
      label: t('roleManagement.descriptionHeader'),
      render: (role) => role.description,
    },
    {
      key: 'permissions',
      label: t('roleManagement.permissionsHeader'),
      render: (role) => {
        if (role.isFarmAdministrator) {
          return <small className="role-management__perm-line">Full access (Farm Administrator)</small>;
        }
        const granted = (role.menus || []).join(', ');
        return (
          <small className="role-management__perm-line">
            {granted || t('roleManagement.none')}
          </small>
        );
      },
    },
    {
      key: 'actions',
      label: t('roleManagement.actionsHeader'),
      render: (role) => (
        <div className="role-management__row-actions">
          <button
            type="button"
            className="pat-btn pat-btn--outline"
            onClick={() => handleOpenModal(role)}
            disabled={Boolean(role.isFarmAdministrator)}
            aria-label="Edit"
          >
            <i className="bi bi-pencil" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--danger"
            onClick={() => handleDelete(role._id)}
            disabled={Boolean(role.isFarmAdministrator)}
            aria-label="Delete"
          >
            <i className="bi bi-trash" aria-hidden="true" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="role-management">
      <div className="role-management__header">
        <h2 className="role-management__heading">{t('roleManagement.heading')}</h2>
        <button
          type="button"
          className="pat-btn pat-btn--primary"
          onClick={() => handleOpenModal()}
        >
          <i className="bi bi-plus-lg" aria-hidden="true" /> {t('roleManagement.newRole')}
        </button>
      </div>

      {error && (
        <Notice tone="danger" action={<button type="button" className="role-management__alert-close" aria-label="Dismiss" onClick={() => setError(null)}>×</button>}>
          <span>{error}</span>
        </Notice>
      )}

      {loading ? (
        <p>{t('roleManagement.loading')}</p>
      ) : (
        <DataTable<Role>
          columns={columns}
          rows={roles}
          rowKey={(role) => role._id}
        />
      )}

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        title={formData._id ? t('roleManagement.editTitle') : t('roleManagement.createTitle')}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <FormSection id="role-meta" title={t('roleManagement.editTitle')}>
              <Field label={t('roleManagement.roleName')} controlId="role-name" required>
                <input
                  id="role-name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('roleManagement.roleNamePlaceholder')}
                  disabled={formData.isFarmAdministrator}
                />
              </Field>
              <Field label={t('roleManagement.description')} controlId="role-description">
                <textarea
                  id="role-description"
                  rows={2}
                  value={formData.description || ''}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  disabled={formData.isFarmAdministrator}
                />
              </Field>
            </FormSection>

            <FormSection
              id="role-menus"
              title={t('roleManagement.functionalPermissions')}
            >
              <RoleEditor
                role={formData}
                onChange={next => setFormData(next)}
              />
            </FormSection>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={handleCloseModal}
            >
              {t('roleManagement.cancel')}
            </button>
            {!formData.isFarmAdministrator && (
              <button type="submit" className="pat-btn pat-btn--primary">
                {t('roleManagement.saveChanges')}
              </button>
            )}
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default RoleManagement;
